import { useEffect, useState, useCallback, useRef, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Loading from './modules/Loading';
import Dashboard from './modules/Dashboard';
import Context from './modules/Context';
import Settings from './modules/Settings';
import Nav from './modules/Nav';
import NewCardForm from './modules/NewCardForm';
import LoginModal from './modules/LoginModal';
import { getCookie, setCookie } from './utils/cookies';
import { CASEDB_DEV_SESSION_REQUIRED_EVENT, clearCaseDbCsrfToken, clearCaseDbDevSessionToken, consumeCaseDbDevSessionFromLocation, isCaseDbLocalDevelopmentOrigin, setCaseDbCsrfToken, startCaseDbDevSessionBridge } from './utils/csrf';
import { CASEDB_APP_ALERT_EVENT } from './utils/appAlerts';
import './styles/Cookies.css';
import './styles/Theme.css';
import './styles/NewCardForm.css';
import Weather from './modules/Weather';
import { PrivacyPolicy } from './modules/PrivacyPolicy';
import { WIP } from './modules/WIP';
import { LayoutEditor } from './modules/LayoutEditor';
import { Contacts } from './modules/Contacts';
import { CustomFields } from './modules/Customs';
import { Leads } from './modules/Leads';
import { Cases } from './modules/Cases';
import { Case } from './modules/Case';
import { BillingSetup } from './modules/BillingSetup';
import { Billing } from './modules/Billing';
import { Timekeeper } from './modules/Timekeeper';
import { UploadProvider } from './modules/UploadManagerContext';
import { UploadTray } from './modules/UploadTray';
import { useUploads } from './modules/UploadManagerContext';
import { PDFViewer } from './modules/PDFViewer';
import DownloadStack from './components/DownloadStack';
import { PFS } from './modules/PFS';
import { Lead } from './modules/Lead';
import SharedViewer from "./pages/SharedViewer";
import { Notifications } from './modules/Notifications';
import Alert from './modules/Alert';
import Accounting from './modules/Accounting';
import { throttle } from './utils/performance';
import Reports from './modules/Reports';
import PublicFormsRoute from './modules/PublicFormsRoute';
import AnalyticsPage from './modules/AnalyticsPage';
import { HomePage } from './modules/Homepage';
import { Docket } from './modules/Docket';
import { Intake } from './modules/Intake';
import FaxSender from './modules/FaxSender';
import Library from './modules/Library';
import ESignatureManager from './modules/ESignatureManager';
import FeedbackModal from './modules/FeedbackModal';
import FileViewer from './modules/FileViewer';
import SearchDeeper from './modules/SearchDeeper';
import { Tasks } from './modules/Tasks';
import NavCompact from './modules/NavCompact';
import NavChangeNotification from './modules/NavChangeNotification';
import FirmSettings from './modules/FirmSettings';
import AI from './modules/AI';
import PersistentJaneDock from './modules/PersistentJaneDock';
import TimeBillingEntryModal from './modules/TimeBillingEntryModal';
import MyTimeBilling from './modules/MyTimeBilling';
import { DocumentGenerationPreferencesProvider } from './modules/DocumentGenerationPreferences';
import { LEGACY_PUBLIC_FORMS_PATH, PUBLIC_FORMS_PATH } from './utils/publicFormsAccess';
import CaseSummaryErrorBoundary from './modules/CaseSummaries/CaseSummaryErrorBoundary';
import { Clock3, EllipsisVertical, MessageCircle, Sparkles } from 'lucide-react';

const CaseSummaryList = lazy(() => import('./modules/CaseSummaries/CaseSummaryList'));
const CaseSummaryPage = lazy(() => import('./modules/CaseSummaries/CaseSummaryPage'));
const DepositionSetup = lazy(() => import('./modules/Depositions/DepositionSetup'));

const CaseSummaryRoute = ({ children }) => (
  <CaseSummaryErrorBoundary>
    <Suspense fallback={<div className="page-container case-analysis-page"><div className="case-analysis-state-card">Loading case analysis…</div></div>}>
      {children}
    </Suspense>
  </CaseSummaryErrorBoundary>
);

const LegacyPublicFormsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`${PUBLIC_FORMS_PATH}${location.search}${location.hash}`} replace />;
};

const shouldSuppressJaneBackgroundPopup = (notification) => {
  if (typeof window === 'undefined') return false;
  const type = String(notification?.type || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!['case_analysis_complete', 'jane_complete', 'jane_failed', 'jane_action_required'].includes(type)) return false;
  const currentPath = String(window.location?.pathname || '').replace(/\/+$/, '') || '/';
  return currentPath === '/ai' || currentPath === '/ai/library';
};

// Create NavVariant context
const NavVariantContext = createContext('compact');
export const useNavVariant = () => useContext(NavVariantContext);

const App = () => {
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState([]);
  const [theme, setTheme] = useState(getCookie('theme') || 'light');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/dashboard?report=thisyear');
  const [user, setUser] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [localDevSessionError, setLocalDevSessionError] = useState(false);
  const sessionCheckInFlightRef = useRef(false);
  const sessionCheckGenerationRef = useRef(0);
  const [navVariant, setNavVariant] = useState(() => {
    // Default to NavCompact unless switched to full
    if (process.env.NODE_ENV === 'development') {
      return 'nav';
    }
    return getCookie('nav_variant_v2') || 'nav';
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setNavVariant('nav');
      setCookie('nav_variant_v2', '', 365);
    }
  }, []);

  useEffect(() => {
    document.documentElement.className = theme;
    setCookie('theme', theme, 365);
  }, [theme]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    // Set appropriate redirect path based on access level
    if (accessLevel === 'storms') {
      setRedirectPath('/dashboard/weather');
    } else if (accessLevel === 'global admin') {
      setRedirectPath('/dashboard?report=thisyear');
    } else {
      setRedirectPath('/dashboard?report=thisyear');
    }
  }, [accessLevel]);

  useEffect(() => {
    // Apply custom styling for storms users
    if (accessLevel === 'storms') {
      // Add storms-specific styling
      const styleElement = document.createElement('style');
      styleElement.id = 'storms-styling';
      styleElement.textContent = `
        .page-container {
          padding: 20px !important;
          margin-left: 0 !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
      `;
      document.head.appendChild(styleElement);
    } else {
      // Remove storms styling if it exists
      const existingStyle = document.getElementById('storms-styling');
      if (existingStyle) {
        existingStyle.remove();
      }
    }

    // Cleanup function to remove styling when component unmounts
    return () => {
      const existingStyle = document.getElementById('storms-styling');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [accessLevel]);

  const changeTheme = () => {
    setTheme(prevTheme => {
      const nextTheme = prevTheme === 'light' ? 'dark' : 'light';
      setCookie('theme', nextTheme, 365);
      return nextTheme;
    });
  };

  useEffect(() => {
    const handleDevSessionRequired = () => {
      if (!isCaseDbLocalDevelopmentOrigin()) return;
      sessionCheckGenerationRef.current += 1;
      setLocalDevSessionError(true);
      setLoggedIn(false);
      setUser(null);
      setAccessLevel(null);
      setLoading(false);
    };
    window.addEventListener(CASEDB_DEV_SESSION_REQUIRED_EVENT, handleDevSessionRequired);
    return () => window.removeEventListener(CASEDB_DEV_SESSION_REQUIRED_EVENT, handleDevSessionRequired);
  }, []);

  const checkUserLoggedIn = useCallback(async () => {
    if (sessionCheckInFlightRef.current) return;
    sessionCheckInFlightRef.current = true;
    const generation = ++sessionCheckGenerationRef.current;
    setLoggedIn(null);
    consumeCaseDbDevSessionFromLocation();

    try {
      const response = await fetch(`https://api.casedb.co/session.php?time=${new Date().getTime()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (generation !== sessionCheckGenerationRef.current) return;
      if (response.ok && data.isLoggedIn === true) {
        setLocalDevSessionError(false);
        setCaseDbCsrfToken(data.csrf_token);
        setLoggedIn(true);
        setAccessLevel(data.access_level);
        setUser(data);
        setCookie('session', 'active', 1);
      } else {
        if (process.env.NODE_ENV === 'development') {
          clearCaseDbCsrfToken();
          clearCaseDbDevSessionToken();
          if (startCaseDbDevSessionBridge()) {
            return;
          }
        }
        clearCaseDbCsrfToken();
        setUser(null);
        setAccessLevel(null);
        setLoggedIn(false);
      }
    } catch (error) {
      if (generation !== sessionCheckGenerationRef.current) return;
      console.error('Error checking user session:', error);
      clearCaseDbCsrfToken();
      setLoggedIn(false);
    } finally {
      sessionCheckInFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUserLoggedIn();
  }, [checkUserLoggedIn]);

  const notisRequestIdRef = useRef(0);
  const notisInFlightRef = useRef(false);
  const knownNotificationIdsRef = useRef(new Set());
  const notificationsInitializedRef = useRef(false);

  useEffect(() => {
    knownNotificationIdsRef.current = new Set();
    notificationsInitializedRef.current = false;
    setNewNotifications([]);
  }, [user?.user_id]);

  useEffect(() => {
    const handleAppAlert = (event) => {
      const detail = event?.detail && typeof event.detail === 'object' ? event.detail : {};
      const alertId = String(detail.id || '').trim();
      if (!alertId) return;

      const appAlert = {
        id: alertId,
        type: String(detail.eventType || 'app_alert'),
        title: String(detail.title || ''),
        body: String(detail.message || ''),
        url: String(detail.actionHref || ''),
        action_label: String(detail.actionLabel || 'View'),
        alert_type: String(detail.type || 'info'),
        duration: Number(detail.duration),
        dismiss_on_click: detail.dismissOnClick !== false,
        show_close: detail.showClose === true || detail.dismissOnClick === false,
        app_alert: true,
      };

      setNewNotifications((current) => {
        if (current.some((notification) => String(notification?.id) === alertId)) return current;
        return [...current, appAlert];
      });
    };

    window.addEventListener(CASEDB_APP_ALERT_EVENT, handleAppAlert);
    return () => window.removeEventListener(CASEDB_APP_ALERT_EVENT, handleAppAlert);
  }, []);

  const fetchNotis = useCallback(async ({ force = false } = {}) => {
    if (notisInFlightRef.current && !force) return;
    notisInFlightRef.current = true;
    const requestId = ++notisRequestIdRef.current;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(`https://api.casedb.co/notifications.php?user_id=${user?.user_id}&time=${new Date().getTime()}`, {
        credentials: 'include',
        signal: controller.signal
      });
      const data = await response.json();

      if (requestId !== notisRequestIdRef.current) {
        return;
      }
      
      // Check if user is still logged in (skip in development mode)
      if (data.stillLoggedIn === false && process.env.NODE_ENV !== 'development') {
        setShowLoginModal(true);
        return;
      }
      
      // If login modal is showing but user is now logged in (logged in from another tab)
      // Close the modal and refresh user data
      if (showLoginModal && data.stillLoggedIn === true) {
        console.log('User logged in from another window, closing login modal');
        setShowLoginModal(false);
        // Refresh the user session data
        checkUserLoggedIn();
      }
      
      const fetchedNotifications = data.notifications || [];

      if (notificationsInitializedRef.current) {
        const arrivingNotifications = fetchedNotifications.filter((notification) => {
          const notificationId = String(notification?.id || '');
          return notificationId !== ''
            && !knownNotificationIdsRef.current.has(notificationId)
            && !shouldSuppressJaneBackgroundPopup(notification);
        });
        if (arrivingNotifications.length > 0) {
          setNewNotifications((current) => {
            const queuedIds = new Set(current.map((notification) => String(notification?.id || '')));
            return [...current, ...arrivingNotifications.filter((notification) => !queuedIds.has(String(notification?.id || '')))];
          });
        }
      } else {
        notificationsInitializedRef.current = true;
      }
      fetchedNotifications.forEach((notification) => {
        if (notification?.id) knownNotificationIdsRef.current.add(String(notification.id));
      });
      
      // Only update state if notifications have actually changed
      setNotifications(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(fetchedNotifications)) {
          return fetchedNotifications;
        }
        return prev;
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.warn('Notifications fetch timed out');
        return;
      }
      console.error('Error fetching notifications:', error);
    }
    finally {
      clearTimeout(timeoutId);
      if (requestId === notisRequestIdRef.current) {
        notisInFlightRef.current = false;
      }
    }
  }, [user?.user_id, showLoginModal, checkUserLoggedIn]);

  useEffect(() => {
    if (user?.user_id) {
      fetchNotis();
      const interval = setInterval(fetchNotis, 10000);
      return () => clearInterval(interval);
    }
  }, [user?.user_id, fetchNotis]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const headers = document.querySelectorAll('#page-header');
      headers.forEach(header => {
        if (window.scrollY > 0) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      });
    }, 16); // ~60fps

    window.addEventListener('scroll', handleScroll);
    
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const logout = async () => {
    if (process.env.NODE_ENV === 'development') {
        return;
    }
    await fetch('https://api.casedb.co/session.php?close', { method: 'GET', credentials: 'include' });
    clearCaseDbCsrfToken();
    clearCaseDbDevSessionToken();
    setLoggedIn(false);
    window.location.href = '/login';
    setShowAlert(true);
  };

  const addCard = (newCard) => {
    setData([...data, newCard]);
  };

  const switchNavVariant = (variant) => {
    setNavVariant(variant);
    if (process.env.NODE_ENV === 'development') {
      setCookie('nav_variant_v2', '', 365);
      return;
    }
    if (variant === 'compact') {
      setCookie('nav_variant_v2', '', 365);
    } else {
      setCookie('nav_variant_v2', variant, 365);
    }
  };

  const handleLoginModalSuccess = (userData) => {
    console.log('handleLoginModalSuccess called with:', userData);
    setCaseDbCsrfToken(userData?.csrf_token);
    setUser(userData);
    setAccessLevel(userData.access_level);
    setLoggedIn(true);
    setShowLoginModal(false);
    // Restart notification polling
    if (userData.user_id) {
      fetchNotis();
    }
  };

  const activeAlertNotification = newNotifications[0] || null;
  const activeAlertType = String(activeAlertNotification?.type || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const isCompletionAlert = ['case_analysis_complete', 'jane_complete'].includes(activeAlertType);
  const isJaneFailureAlert = activeAlertType === 'jane_failed';
  const isJaneActionAlert = activeAlertType === 'jane_action_required';
  const isJaneBackgroundAlert = isCompletionAlert || isJaneFailureAlert || isJaneActionAlert;
  const isAppAlert = activeAlertNotification?.app_alert === true;
  const activeAlertDuration = isAppAlert && Number.isFinite(activeAlertNotification?.duration) && activeAlertNotification.duration >= 0
    ? activeAlertNotification.duration
    : isJaneBackgroundAlert ? 15000 : 5000;
  const activeAlertDismissOnClick = isAppAlert
    ? activeAlertNotification.dismiss_on_click !== false
    : !isJaneBackgroundAlert;
  const activeAlertShowClose = isAppAlert
    ? activeAlertNotification.show_close === true
    : isJaneBackgroundAlert;
  const activeAlertActionHref = isAppAlert
    ? activeAlertNotification.url || undefined
    : isJaneBackgroundAlert ? activeAlertNotification.url : undefined;
  const closeActiveNotificationAlert = () => {
    setNewNotifications((current) => current.filter((notification) => String(notification?.id) !== String(activeAlertNotification?.id)));
  };

  if (loading || loggedIn === null) {
    return <Loading />;
  }

  return (
    <>
      {localDevSessionError && !loggedIn && (
        <div role="alert" className="page-container">
          <p>Local session verification failed after reconnecting. Automatic retries have stopped. Sign in to CaseDB in this browser, then retry the local connection.</p>
          <button type="button" className="action" onClick={() => startCaseDbDevSessionBridge({ userInitiated: true })}>Retry local connection</button>
        </div>
      )}
      <UploadProvider>
        <NavVariantContext.Provider value={navVariant}>
          <DocumentGenerationPreferencesProvider loggedIn={loggedIn}>
            <Router>
              <ConditionalNav accessLevel={accessLevel} notifications={notifications} fetchNotifications={fetchNotis} user={user} loggedIn={loggedIn} changeTheme={changeTheme} theme={theme} logout={logout} data={data} setData={setData} setFilteredData={setFilteredData} navVariant={navVariant} switchNavVariant={switchNavVariant} />
              <AppRoutes authSuspended={showLoginModal} accessLevel={accessLevel} setAccessLevel={setAccessLevel} notifications={notifications} fetchNotifications={fetchNotis} setUser={setUser} user={user} loggedIn={loggedIn} setLoggedIn={setLoggedIn} changeTheme={changeTheme} theme={theme} data={filteredData} setData={setData} addCard={addCard} logout={logout} setFilteredData={setFilteredData} setShowAlert={setShowAlert} showAlert={showAlert} redirectPath={redirectPath} setRedirectPath={setRedirectPath} navVariant={navVariant} switchNavVariant={switchNavVariant} />
              <PersistentJaneDock user={user} loggedIn={loggedIn} hideLauncher />
              <DownloadStack />
              <GlobalUploadTray />
              {loggedIn && <GlobalFloatingActions accessLevel={accessLevel} />}
              {loggedIn && <NavChangeNotification />}
            </Router>
          </DocumentGenerationPreferencesProvider>
        </NavVariantContext.Provider>
      </UploadProvider>
      {activeAlertNotification && (
        <Alert
          title={activeAlertNotification.title || (isCompletionAlert ? 'Background work complete' : isJaneFailureAlert ? 'Background run failed' : isJaneActionAlert ? 'Review needed' : 'New notification')}
          message={activeAlertNotification.body || (isCompletionAlert ? 'Your result is ready to review.' : isJaneFailureAlert ? 'Open Jane to review the run.' : isJaneActionAlert ? 'Jane is waiting for your review.' : 'You have a new notification.')}
          type={isAppAlert ? activeAlertNotification.alert_type : isCompletionAlert ? 'success' : isJaneFailureAlert ? 'error' : 'notifications'}
          duration={activeAlertDuration}
          dismissOnClick={activeAlertDismissOnClick}
          showClose={activeAlertShowClose}
          actionHref={activeAlertActionHref}
          actionLabel={isAppAlert ? activeAlertNotification.action_label : activeAlertType === 'case_analysis_complete' ? 'View analysis' : 'Open in Jane'}
          onClose={closeActiveNotificationAlert}
        />
      )}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        onLoginSuccess={handleLoginModalSuccess}
      />
    </>
  );
}

const ConditionalNav = ({ accessLevel, user, notifications, fetchNotifications, loggedIn, changeTheme, theme, logout, data, setData, setFilteredData, navVariant, switchNavVariant }) => {
  const location = useLocation();
  const pathName = location.pathname.substring(1);

  if (!loggedIn) {
    return null;
  }

  // Hide navigation for storms users - they only get the weather page
  if (accessLevel === 'storms') {
    return null;
  }

  const formatTitle = (path) => {
    return path
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const title = formatTitle(pathName);

  // Additional check for login or signup pages (although this shouldn't be reached when logged out)
  if (pathName === 'login' || pathName === 'signup') {
    return null;
  }

  // Render the appropriate nav based on variant
  if (navVariant === 'compact' || navVariant === 'nav') {
    return <NavCompact title={title || 'Dashboard'} notifications={notifications} fetchNotifications={fetchNotifications} accessLevel={accessLevel} user={user?.user} loggedIn={loggedIn} changeTheme={changeTheme} theme={theme} logout={logout} switchNavVariant={switchNavVariant} />;
  } else {
    return <Nav notifications={notifications} fetchNotifications={fetchNotifications} accessLevel={accessLevel} loggedIn={loggedIn} changeTheme={changeTheme} theme={theme} logout={logout} />;
  }
};

const AppRoutes = ({ authSuspended = false, accessLevel, setAccessLevel, notifications, fetchNotifications, setUser, user, loggedIn, setLoggedIn, changeTheme, theme, data, setData, addCard, logout, setFilteredData, setShowAlert, showAlert, redirectPath, setRedirectPath, navVariant, switchNavVariant }) => {
  const location = useLocation();

  useEffect(() => {
    if (!loggedIn && location.pathname !== '/login' && location.pathname !== '/signup') {
      setRedirectPath(location.pathname + location.search);
    }
  }, [loggedIn, location, setRedirectPath]);

  if (loggedIn === null) {
    return <Loading />;
  }

  return (
    <Routes>
      {loggedIn === true ? (
        <>
          {accessLevel === 'storms' ? (
            // Storms users can only access the weather page
            <>
              <Route path="/dashboard/weather" element={<Weather theme={theme} />} />
              <Route path="*" element={<Navigate to="/dashboard/weather" />} />
            </>
          ) : (
            // All other users get full access
            <>
              <Route path="/cases" element={<Cases user={user} />} />
              <Route path="/case/:id" element={<Case id={":id"} user_id={user.user_id}/>} />
              <Route path="/leads" element={<Leads user={user} />} />
              <Route path="/intake" element={<Intake user={user} />} />
              <Route path='/intake/:id' element={<Lead user={user} />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/contacts/:contactId" element={<Contacts />} />
              <Route path="/contact/:contactId" element={<Contacts />} />
              <Route path="/client-portal" element={<WIP />} />
              <Route path="/firm-settings" element={<FirmSettings />} />
              <Route path="/firm-settings/:pageIndex" element={<FirmSettings />} />
              <Route path="/dashboard/weather" element={<Weather theme={theme} />} />
              <Route path="/settings" element={<Settings user={user} setUser={setUser} changeTheme={changeTheme} theme={theme} navVariant={navVariant} switchNavVariant={switchNavVariant} />} />
              <Route path="/new-data" element={<NewCardForm addCard={addCard} />} />
              <Route path="*" element={<Navigate to={redirectPath} />} />
              <Route path="/google" element={<Dashboard google={true} />} />
              <Route path="/ai" element={<AI user={user} authSuspended={authSuspended} />} />
              <Route path="/ai/library" element={<AI user={user} authSuspended={authSuspended} />} />
              <Route path='/library' element={<Library user={user} />} />
              <Route path='/library/pfs' element={<PFS />} />
              <Route path='/library/:fileId' element={<PDFViewer />} />
              <Route path='timekeeper' element={<Timekeeper user_id={user.user_id} />} />
              <Route path="/notifications" element={<Notifications notifications={notifications} fetchNotifications={fetchNotifications} />} />
              <Route path="/reports" element={<Reports accessLevel={accessLevel} />} />
              <Route path={PUBLIC_FORMS_PATH} element={<PublicFormsRoute accessLevel={accessLevel} navVariant={navVariant} />} />
              <Route path={LEGACY_PUBLIC_FORMS_PATH} element={<LegacyPublicFormsRedirect />} />
              <Route path="/accounting" element={<Accounting />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/time-billing" element={<MyTimeBilling navVariant={navVariant} />} />
              <Route path="/fax" element={<FaxSender />} />
              <Route path="/docket" element={<Docket />} />
              <Route path="/docket/:id" element={<Docket />} />
              <Route path="/file/:fileId" element={<FileViewer />} />
              <Route path="/sign" element={<ESignatureManager />} />
              <Route path="/search" element={<SearchDeeper />} />
              <Route path="/summaries" element={<CaseSummaryRoute><CaseSummaryList /></CaseSummaryRoute>} />
              <Route path="/summaries/:caseId" element={<CaseSummaryRoute><CaseSummaryPage /></CaseSummaryRoute>} />
              <Route path="/case/:caseId/depositions/new" element={<CaseSummaryRoute><DepositionSetup /></CaseSummaryRoute>} />
              <Route path="/case/:caseId/depositions/:depositionId" element={<CaseSummaryRoute><DepositionSetup /></CaseSummaryRoute>} />
              <Route path="/dashboard" element={<HomePage user={user} />} />
              {accessLevel === 'global admin' ? (
                <>
                  <Route path='/home' element={<Dashboard setLoggedIn={setLoggedIn} google={false} />} />
                  <Route path='/firm-settings/billing' element={<Billing />} />
                  <Route path='/firm-settings/billing-setup' element={<BillingSetup />} />
                  <Route path="/firm-settings/custom-fields" element={<CustomFields />} />
                  <Route path="/firm-settings/layout-editor" element={<LayoutEditor />} />
                  <Route path='/firm-settings/task-workflows' element={<Tasks />} />
                </>
              ) : (
                <Route path='/dashboard' element={<Cases user={user} />} />
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Route path="/login" element={<Context setAccessLevel={setAccessLevel} setUser={setUser} setLoggedIn={setLoggedIn} setShowAlert={setShowAlert} showAlert={showAlert} />} />
          <Route path="/signup" element={<Context setAccessLevel={setAccessLevel} setLoggedIn={setLoggedIn} setShowAlert={setShowAlert} showAlert={showAlert} />} />
          <Route path="*" element={<Navigate to="/login" state={{ from: location.pathname + location.search }} />} />
        </>
      )}
      <Route path="/share/:token" element={<SharedViewer />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    </Routes>
  );
}

const GlobalUploadTray = () => {
  const { uploads } = useUploads();
  return <UploadTray uploads={uploads} />;
}

const GLOBAL_FLOATING_ACTIONS_EXPANDED_KEY = 'global-floating-actions-expanded';

const GlobalFloatingActions = ({ accessLevel }) => {
  const location = useLocation();
  const canUseTimeBilling = !['', 'no access', 'storms'].includes(
    String(accessLevel || '').trim().toLowerCase()
  );
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(GLOBAL_FLOATING_ACTIONS_EXPANDED_KEY) === '1';
  });
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showTimeBillingModal, setShowTimeBillingModal] = useState(false);

  const setPersistedExpanded = useCallback((nextValue) => {
    setExpanded(nextValue);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(GLOBAL_FLOATING_ACTIONS_EXPANDED_KEY, nextValue ? '1' : '0');
    }
  }, []);

  const openJane = () => {
    window.dispatchEvent(new CustomEvent('openPersistentJaneDock', {
      detail: { focusComposer: true }
    }));
    setPersistedExpanded(false);
  };

  const openFeedback = () => {
    setShowFeedbackModal(true);
    setPersistedExpanded(false);
  };

  const openTimeBilling = () => {
    setShowTimeBillingModal(true);
    setPersistedExpanded(false);
  };

  return (
    <>
      <div className={`global-floating-actions${expanded ? ' expanded' : ''}`}>
        {expanded && (
          <div className="global-floating-actions-menu" aria-label="Quick actions">
            <button
              type="button"
              className="global-floating-action-button"
              onClick={openJane}
              title="Open Jane"
              aria-label="Open Jane"
            >
              <Sparkles size={20} />
            </button>
            {canUseTimeBilling && (
              <button
                type="button"
                className="global-floating-action-button"
                onClick={openTimeBilling}
                title="Add time"
                aria-label="Add time"
              >
                <Clock3 size={20} />
              </button>
            )}
            <button
              type="button"
              className="global-floating-action-button"
              onClick={openFeedback}
              title="Send feedback"
              aria-label="Send feedback"
            >
              <MessageCircle size={20} />
            </button>
          </div>
        )}
        <button
          type="button"
          className="global-floating-actions-toggle"
          onClick={() => setPersistedExpanded(!expanded)}
          aria-label={expanded ? 'Hide quick actions' : 'Show quick actions'}
          aria-expanded={expanded}
          title="Quick actions"
        >
          <EllipsisVertical size={22} />
        </button>
      </div>

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          currentPage={location.pathname + location.search}
        />
      )}
      {canUseTimeBilling && showTimeBillingModal && (
        <TimeBillingEntryModal onClose={() => setShowTimeBillingModal(false)} />
      )}
    </>
  );
}

export default App;
